"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_ATTACK_DELAY_TICKS,
  CpuPilot,
  LegacyGame,
  POSE_CELLS,
  getPoseCells,
  type GameEvent,
  type InputControl,
  type PieceId,
} from "../../lib/game";
import { applyInputToActiveGame, quantizeInputTransitionTick } from "../battle-boundaries";
import { recordLossAndMaybeShowInterstitial } from "../admob";
import { useLegacyAudio } from "../hooks/useLegacyAudio";
import { useLegacyKeyboard, type KeyBindings } from "../hooks/useLegacyKeyboard";
import { BattleArena, type BoardView, type MatchStatus } from "./BattleArena";
import { AdSlot } from "./AdSlot";
import type { LegacyAction } from "./TouchControls";

type CpuModeProps = {
  keyBindings: KeyBindings;
  onBack: () => void;
  opponentName?: string;
  humanSeat?: 1 | 2;
  returnHomeAfterResult?: boolean;
  returnToRoomAfterResult?: boolean;
  onResult?: (result: CpuResult) => void;
};

type QueuedCpuMatchInput = {
  applyBeforeTick: number;
  control: InputControl;
  held: boolean;
  sequence: number;
};

export type CpuResult = "win" | "loss" | "draw";

type Pair = {
  human: LegacyGame;
  cpu: LegacyGame;
};

type CpuStats = {
  lines: number;
  pieces: number;
  received: number;
};

// Mobile Safari can pause requestAnimationFrame while the browser chrome is
// moving or the app briefly loses foreground priority. Never try to simulate
// an unbounded backlog in one frame; rebasing the clock below keeps the match
// responsive when that happens.
const MAX_CPU_CATCH_UP_TICKS = 240;

const ACTION_TO_INPUT: Readonly<Record<LegacyAction, InputControl>> = {
  ccw: "rotateCCW",
  cw: "rotateCW",
  left: "left",
  right: "right",
  softDrop: "softDrop",
  hardDrop: "hardDrop",
};

function nextCells(piece: number): ReadonlyArray<readonly [number, number]> {
  if (piece < 1 || piece > 7) return [];
  return POSE_CELLS[piece as PieceId][0].map(({ dx, dy }) => [dx, dy] as const);
}

function viewFromGame(game: LegacyGame, received: number): BoardView {
  const active = game.active;
  return {
    active: getPoseCells(active).map(({ x, y }) => ({ x, y, piece: active.piece })),
    blockIndex: game.blockIndex,
    board: game.board,
    nextCells: nextCells(game.nextPiece),
    nextPiece: game.nextPiece,
    pending: game.pendingGarbageLines,
    received,
    topOut: game.topOut,
  };
}

function newCpuSeed(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(
    crypto.getRandomValues(new Uint8Array(16)),
    (byte) => byte.toString(16).padStart(2, "0"),
  ).join("");
}

function createPair(): Pair {
  const seed = newCpuSeed();
  const matchId = `cpu:${Date.now()}`;
  return {
    human: new LegacyGame({ matchId: `${matchId}:human`, seed }),
    cpu: new LegacyGame({ matchId: `${matchId}:cpu`, seed }),
  };
}

function resultLabel(result: CpuResult): string {
  if (result === "win") return "YOU WIN";
  if (result === "loss") return "YOU LOSE";
  return "DRAW";
}

export function CpuMode({ keyBindings, onBack, opponentName = "CPU", humanSeat = 1, returnHomeAfterResult = false, returnToRoomAfterResult = false, onResult }: CpuModeProps) {
  const initialPair = useState(createPair)[0];
  const humanGameRef = useRef(initialPair.human);
  const cpuGameRef = useRef(initialPair.cpu);
  const pilotRef = useRef(new CpuPilot("normal"));
  const originTimeRef = useRef(0);
  const queuedInputsRef = useRef<QueuedCpuMatchInput[]>([]);
  const inputSequenceRef = useRef(0);
  const inputOrderRef = useRef(0);
  const requestedInputsRef = useRef<Record<InputControl, boolean>>({
    left: false,
    right: false,
    rotateCCW: false,
    rotateCW: false,
    softDrop: false,
    hardDrop: false,
  });
  const topOutRef = useRef<{ human?: number; cpu?: number }>({});
  const resultRef = useRef<CpuResult | undefined>(undefined);
  const runningRef = useRef(true);
  const [humanView, setHumanView] = useState<BoardView>(() => viewFromGame(initialPair.human, 0));
  const [cpuView, setCpuView] = useState<BoardView>(() => viewFromGame(initialPair.cpu, 0));
  const [humanStats, setHumanStats] = useState<CpuStats>({ lines: 0, pieces: 0, received: 0 });
  const [cpuStats, setCpuStats] = useState<CpuStats>({ lines: 0, pieces: 0, received: 0 });
  const humanReceivedRef = useRef(0);
  const cpuReceivedRef = useRef(0);
  const [seconds, setSeconds] = useState(0);
  const [status, setStatus] = useState(returnHomeAfterResult ? "CASUAL CPU — 対戦中" : "CPU NORMAL — 対戦中");
  const [running, setRunning] = useState(true);
  const [result, setResult] = useState<CpuResult | null>(null);
  const [resultDetail, setResultDetail] = useState("");
  const { playLock, unlock } = useLegacyAudio();

  const settleResult = useCallback(() => {
    if (resultRef.current) return;
    const { human, cpu } = topOutRef.current;
    if (human === undefined && cpu === undefined) return;

    const nextResult: CpuResult =
      human !== undefined && cpu !== undefined
        ? human === cpu ? "draw" : human < cpu ? "loss" : "win"
        : human !== undefined ? "loss" : "win";
    const detail =
      nextResult === "draw"
        ? `同一tick (${human}) で両者TOP OUT`
        : nextResult === "win"
          ? `CPUがtick ${cpu} でTOP OUT`
          : `自分がtick ${human} でTOP OUT`;

    resultRef.current = nextResult;
    if (nextResult === "loss") recordLossAndMaybeShowInterstitial();
    runningRef.current = false;
    setRunning(false);
    setResult(nextResult);
    setResultDetail(detail);
    setStatus("FINISH");
    onResult?.(nextResult);
  }, [onResult]);

  const processEvents = useCallback((owner: "human" | "cpu", events: readonly GameEvent[]) => {
    for (const event of events) {
      if (event.type === "lock") {
        playLock(event.clearedLines);
        if (owner === "human") {
          humanReceivedRef.current += event.garbageApplied;
          setHumanStats((stats) => ({
            lines: stats.lines + event.clearedLines,
            pieces: stats.pieces + 1,
            received: humanReceivedRef.current,
          }));
          if (event.attackLines > 0) {
            cpuGameRef.current.applyScheduledAttack({
              attackId: `human:${event.lockSeq}`,
              createdTick: event.tick,
              eligibleTick: event.tick + DEFAULT_ATTACK_DELAY_TICKS,
              lines: event.attackLines,
              sourceLockSeq: event.lockSeq,
            });
          }
        } else {
          cpuReceivedRef.current += event.garbageApplied;
          setCpuStats((stats) => ({
            lines: stats.lines + event.clearedLines,
            pieces: stats.pieces + 1,
            received: cpuReceivedRef.current,
          }));
          if (event.attackLines > 0) {
            humanGameRef.current.applyScheduledAttack({
              attackId: `cpu:${event.lockSeq}`,
              createdTick: event.tick,
              eligibleTick: event.tick + DEFAULT_ATTACK_DELAY_TICKS,
              lines: event.attackLines,
              sourceLockSeq: event.lockSeq,
            });
          }
        }
      } else if (event.type === "topout") {
        topOutRef.current[owner] = event.tick;
      }
    }
  }, [playLock]);

  const applyHumanInputsForNextTick = useCallback(() => {
    const game = humanGameRef.current;
    while (queuedInputsRef.current[0]?.applyBeforeTick <= game.tick + 1) {
      const queued = queuedInputsRef.current.shift();
      if (!queued) break;
      const nextSequence = inputSequenceRef.current + 1;
      if (applyInputToActiveGame(game, queued.control, queued.held, nextSequence)) {
        inputSequenceRef.current = nextSequence;
      }
    }
  }, []);

  const advanceTo = useCallback((targetTick: number) => {
    const human = humanGameRef.current;
    const cpu = cpuGameRef.current;
    const currentTick = Math.max(human.tick, cpu.tick);
    const catchUpTarget = Math.min(targetTick, currentTick + MAX_CPU_CATCH_UP_TICKS);
    while (
      !resultRef.current
      && ((human.tick < catchUpTarget && !human.topOut) || (cpu.tick < catchUpTarget && !cpu.topOut))
    ) {
      const nextTick = Math.min(
        human.tick < catchUpTarget && !human.topOut ? human.tick + 1 : Number.POSITIVE_INFINITY,
        cpu.tick < catchUpTarget && !cpu.topOut ? cpu.tick + 1 : Number.POSITIVE_INFINITY,
      );
      if (!Number.isFinite(nextTick)) break;

      if (human.tick < nextTick && !human.topOut) {
        applyHumanInputsForNextTick();
        processEvents("human", human.stepTo(nextTick));
      }
      if (cpu.tick < nextTick && !cpu.topOut) {
        pilotRef.current.sync(cpu);
        processEvents("cpu", cpu.stepTo(nextTick));
      }
      settleResult();
    }

    if (!resultRef.current && targetTick > catchUpTarget) {
      // Drop an old background-tab backlog instead of blocking the main
      // thread. The next frame resumes from the latest simulated tick.
      originTimeRef.current = performance.now() - (Math.min(human.tick, cpu.tick) / 0.12);
    }

    // Keep the result path safe even if a defensive top-out was produced
    // without a normal lock event (for example after a late garbage update).
    if (!resultRef.current) {
      if (human.topOut && topOutRef.current.human === undefined) topOutRef.current.human = human.tick;
      if (cpu.topOut && topOutRef.current.cpu === undefined) topOutRef.current.cpu = cpu.tick;
      settleResult();
    }
  }, [applyHumanInputsForNextTick, processEvents, settleResult]);

  const handleInput = useCallback((action: LegacyAction, held: boolean) => {
    void unlock();
    if (!runningRef.current || resultRef.current) return;
    const control = ACTION_TO_INPUT[action];
    if (requestedInputsRef.current[control] === held) return;
    requestedInputsRef.current[control] = held;
    const game = humanGameRef.current;
    const applyBeforeTick = quantizeInputTransitionTick(
      game.startTick,
      game.tick,
      control,
      queuedInputsRef.current,
    );
    queuedInputsRef.current.push({
      applyBeforeTick,
      control,
      held,
      sequence: ++inputOrderRef.current,
    });
    queuedInputsRef.current.sort(
      (left, right) => left.applyBeforeTick - right.applyBeforeTick || left.sequence - right.sequence,
    );
  }, [unlock]);

  useLegacyKeyboard(running && !result, handleInput, "A", keyBindings);

  useEffect(() => {
    originTimeRef.current = performance.now();
    let frame = 0;
    const render = () => {
      if (runningRef.current && !resultRef.current) {
        const targetTick = Math.floor((performance.now() - originTimeRef.current) * 0.12);
        if (targetTick > humanGameRef.current.tick || targetTick > cpuGameRef.current.tick) {
          advanceTo(targetTick);
        }
        setSeconds(Math.floor(Math.min(humanGameRef.current.tick, cpuGameRef.current.tick) / 120));
      }
      setHumanView(viewFromGame(humanGameRef.current, humanReceivedRef.current));
      setCpuView(viewFromGame(cpuGameRef.current, cpuReceivedRef.current));
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [advanceTo]);

  const resetGame = useCallback(() => {
    const pair = createPair();
    humanGameRef.current = pair.human;
    cpuGameRef.current = pair.cpu;
    pilotRef.current.reset();
    originTimeRef.current = performance.now();
    queuedInputsRef.current = [];
    inputSequenceRef.current = 0;
    inputOrderRef.current = 0;
    requestedInputsRef.current = {
      left: false,
      right: false,
      rotateCCW: false,
      rotateCW: false,
      softDrop: false,
      hardDrop: false,
    };
    topOutRef.current = {};
    resultRef.current = undefined;
    runningRef.current = true;
    humanReceivedRef.current = 0;
    cpuReceivedRef.current = 0;
    setHumanView(viewFromGame(pair.human, 0));
    setCpuView(viewFromGame(pair.cpu, 0));
    setHumanStats({ lines: 0, pieces: 0, received: 0 });
    setCpuStats({ lines: 0, pieces: 0, received: 0 });
    setSeconds(0);
    setRunning(true);
    setResult(null);
    setResultDetail("");
    setStatus(returnHomeAfterResult ? "CASUAL CPU — 対戦中" : "CPU NORMAL — 対戦中");
  }, [returnHomeAfterResult]);

  const handleRestart = async () => {
    await unlock();
    resetGame();
  };

  useEffect(() => {
    if (!(returnHomeAfterResult || returnToRoomAfterResult) || !result) return;
    const timer = window.setTimeout(onBack, 1_700);
    return () => window.clearTimeout(timer);
  }, [onBack, result, returnHomeAfterResult, returnToRoomAfterResult]);

  const humanStatus: MatchStatus = {
    group: humanSeat === 1 ? "P1" : "P2",
    text: `YOU  ${humanStats.lines}LINES / ${humanStats.pieces}PIECES`,
  };
  const cpuStatus: MatchStatus = {
    group: humanSeat === 1 ? "P2" : "P1",
    text: `NORMAL  ${cpuStats.lines}LINES / ${cpuStats.pieces}PIECES`,
  };
  const p1IsHuman = humanSeat === 1;

  return (
    <main className="cpu-shell">
      <header className="cpu-header mode-header">
        <button className="back-button" onClick={onBack} type="button">← メニュー</button>
        <span className="mode-header-label">{returnHomeAfterResult ? "CASUAL MATCH / BLOCK BATTLE" : returnToRoomAfterResult ? "ROOM CPU / BLOCK BATTLE" : "CPU BATTLE"}</span>
        <span className="cpu-header-status"><span className="status-dot" /> {status}</span>
      </header>

      <div className="cpu-match-stage">
        <BattleArena
          controlsEnabled={running && !result}
          localSeat={humanSeat}
          onInput={handleInput}
          p1={p1IsHuman ? humanView : cpuView}
          p2={p1IsHuman ? cpuView : humanView}
          p1Label={p1IsHuman ? "PLAYER 1 / YOU" : `${opponentName} / NORMAL`}
          p2Label={p1IsHuman ? `${opponentName} / NORMAL` : "PLAYER 2 / YOU"}
          p1Status={humanStatus}
          p2Status={cpuStatus}
          quality={`LOCAL CPU / ${String(seconds).padStart(2, "0")}s`}
        />
      </div>

      {result ? (
        <div className="result-panel">
          <section className="result-card">
            <h2>{resultLabel(result)}</h2>
            <p>{resultDetail}</p>
            {returnHomeAfterResult || returnToRoomAfterResult ? <p>{returnHomeAfterResult ? "ホームへ戻ります…" : "ルームへ戻ります…"}</p> : (
              <button className="chrome-button" onClick={() => void handleRestart()} type="button">
                もう一度
              </button>
            )}
          </section>
          <AdSlot format="horizontal" label="試合結果の広告スペース" />
        </div>
      ) : null}
    </main>
  );
}
